import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert, Box, Button, CircularProgress, FormControl,
  InputLabel, MenuItem, Select, TextField, Typography
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import { portfolioApi, tradesApi } from '../../services/api'

const DIRECTIONS = ['LONG', 'SHORT']
const ASSET_TYPES = ['STOCK', 'CRYPTO', 'FOREX', 'FUTURES', 'OPTION', 'CFD']

interface TradeFormData {
  portfolioId: string
  symbol: string
  direction: string
  assetType: string
  quantity: string
  entryPrice: string
  notes: string
}

const defaultForm: TradeFormData = {
  portfolioId: '',
  symbol: '',
  direction: 'LONG',
  assetType: 'STOCK',
  quantity: '',
  entryPrice: '',
  notes: '',
}

export default function TradeForm() {
  const navigate = useNavigate()
  const [form, setForm] = useState<TradeFormData>(defaultForm)
  const [error, setError] = useState<string | null>(null)

  const { data: portfolioData } = useQuery({
    queryKey: ['portfolios'],
    queryFn: () => portfolioApi.list().then((r) => r.data),
  })
  const portfolios = portfolioData?.content ?? []

  const mutation = useMutation({
    mutationFn: (data: TradeFormData) =>
      tradesApi.create({
        portfolioId: data.portfolioId,
        symbol: data.symbol.toUpperCase(),
        direction: data.direction,
        assetType: data.assetType,
        quantity: parseFloat(data.quantity),
        entryPrice: parseFloat(data.entryPrice),
        notes: data.notes || undefined,
      }),
    onSuccess: () => navigate('/trades'),
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        'Failed to create trade'
      setError(msg)
    },
  })

  const handleChange = (field: keyof TradeFormData) => (
    e: React.ChangeEvent<HTMLInputElement | { value: unknown }>
  ) => setForm((prev) => ({ ...prev, [field]: e.target.value as string }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    mutation.mutate(form)
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 560 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
        New Trade Entry
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Portfolio</InputLabel>
        <Select
          value={form.portfolioId}
          label="Portfolio"
          onChange={(e) => setForm((prev) => ({ ...prev, portfolioId: e.target.value }))}
          required
        >
          {portfolios.map((p: { id: string; name: string }) => (
            <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth label="Symbol" value={form.symbol}
        onChange={handleChange('symbol')}
        required inputProps={{ style: { textTransform: 'uppercase' } }}
        sx={{ mb: 2 }}
      />

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Direction</InputLabel>
        <Select
          value={form.direction} label="Direction"
          onChange={(e) => setForm((prev) => ({ ...prev, direction: e.target.value }))}
        >
          {DIRECTIONS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
        </Select>
      </FormControl>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Asset Type</InputLabel>
        <Select
          value={form.assetType} label="Asset Type"
          onChange={(e) => setForm((prev) => ({ ...prev, assetType: e.target.value }))}
        >
          {ASSET_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </Select>
      </FormControl>

      <TextField
        fullWidth label="Quantity" type="number" value={form.quantity}
        onChange={handleChange('quantity')}
        required inputProps={{ min: 0, step: 'any' }}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth label="Entry Price" type="number" value={form.entryPrice}
        onChange={handleChange('entryPrice')}
        required inputProps={{ min: 0, step: 'any' }}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth label="Notes (optional)" value={form.notes}
        onChange={handleChange('notes')}
        multiline rows={3}
        sx={{ mb: 3 }}
      />

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          type="submit" variant="contained" color="primary"
          disabled={mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} /> : null}
        >
          {mutation.isPending ? 'Submitting…' : 'Submit Trade'}
        </Button>
        <Button variant="outlined" onClick={() => navigate('/trades')}>
          Cancel
        </Button>
      </Box>
    </Box>
  )
}
